/**
 * Decision 0144 (experiment 11, 0136): one shape for every ask and update, and what a ref chip shows.
 *
 * `CardShape` is a card in the order every surface renders it: the question, why it matters, the recommendation and
 * its reason (above the answers), each answer with what happens next, who it waits on, details folded. Core builds it
 * from the state (`cardShape`); the web renders it in a project, the ref lookup sends it to Home and the bell, and
 * notifications and push take their title and line from it.
 *
 * `RefView` is what a chip and its preview show for a ref (type icon, title, one status mark, the facts), with the
 * card's shape for a card. No I/O.
 */
import { Schema } from "effect"
import { ProposalDecision } from "./commands/index"
import { Millis, ProposalId } from "./ids"

/** One status mark: a shape each, so colour is never the only signal. */
export const RefMark = Schema.Literals(["needs", "live", "blocked", "wait", "done", "gone", "none"])
export type RefMark = typeof RefMark.Type

/** What kind of thing a ref is (the chip's icon). */
export const RefType = Schema.Literals([
  "item",
  "card",
  "run",
  "validator",
  "output",
  "goal",
  "kpi",
  "memory",
  "decision",
])
export type RefType = typeof RefType.Type

export const CardAnswer = Schema.Struct({
  /** A stable key: the option key, or approve / reject / request_revision. */
  key: Schema.String,
  label: Schema.String,
  /** What happens next when this answer is picked ("PR 1 starts on the Mac when it's awake"). */
  outcome: Schema.NullOr(Schema.String),
  recommended: Schema.Boolean,
  /** The decision a click sends; null when the answer needs input first (a note, picks): the full card asks for it. */
  decision: Schema.NullOr(ProposalDecision),
  /** The answer needs a note (Request changes): the popover asks for it before sending. */
  note: Schema.optionalKey(Schema.Literals(["required", "optional"])),
  destructive: Schema.optionalKey(Schema.Boolean),
})
export type CardAnswer = typeof CardAnswer.Type

export const CardShape = Schema.Struct({
  proposalId: ProposalId,
  ref: Schema.String,
  /** The card kind word: "Plan change", "Question", "Review", "Memory", "Permission", … */
  kindWord: Schema.String,
  /** The proposal kind (`plan_change`, `question`, …). */
  kind: Schema.String,
  /** Who proposed it ("Bob"); null for Minga's own cards. */
  from: Schema.NullOr(Schema.String),
  revision: Schema.Int,
  contentHash: Schema.String,
  open: Schema.Boolean,
  /** The question a person answers. */
  ask: Schema.String,
  /** Why it matters (what it blocks, a date, a risk), one or two lines; null when nothing to add. */
  why: Schema.NullOr(Schema.String),
  /** How many open items wait on it. */
  blocks: Schema.Int,
  recommendation: Schema.NullOr(
    Schema.Struct({
      /** Who recommends ("Lea"). */
      by: Schema.NullOr(Schema.String),
      /** The recommended answer's label ("Approve"), or null when the recommendation names no answer. */
      label: Schema.NullOr(Schema.String),
      key: Schema.NullOr(Schema.String),
      reason: Schema.String,
    }),
  ),
  answers: Schema.Array(CardAnswer),
  /** Who it waits on ("you", "Becky", "Lea") and since when. */
  waitsOn: Schema.String,
  waitsOnViewer: Schema.Boolean,
  since: Millis,
  /** The proposer's full reasoning and the chain, for Details (folded). */
  details: Schema.String,
  /**
   * Decided in place (popover, brief, bell) or on its page: plans over five changes, outward actions, permission
   * prompts and reviews with artifacts go to the full page (0136 §1).
   */
  inPlace: Schema.Boolean,
  /** The recommended answer may be sent from a notification (push action, bell button). */
  fromNotification: Schema.Boolean,
  /** Decided: who, what and when ("Approved by you"). Null while open. */
  decided: Schema.NullOr(Schema.Struct({ line: Schema.String, at: Millis })),
  /** The decision's ref (D-n) once decided. */
  decisionRef: Schema.NullOr(Schema.String),
})
export type CardShape = typeof CardShape.Type

export const RefView = Schema.Struct({
  ref: Schema.String,
  type: RefType,
  /** The variant word: "Task", "Bug", "Plan change", "Worker run", "Report", … */
  variant: Schema.String,
  title: Schema.String,
  /** The status in words ("Ready, waits on you", "Running", "Done"). */
  status: Schema.String,
  mark: RefMark,
  /** The app path of its page (same origin). */
  href: Schema.String,
  projectName: Schema.String,
  /** Facts for the preview: Owner, Blocks, Agent, … in order. */
  facts: Schema.Array(Schema.Struct({ label: Schema.String, value: Schema.String })),
  /** A card's shape (P-n, and a decided card's D-n). */
  card: Schema.optionalKey(CardShape),
  /**
   * How much it holds up (core `askRank` for a card, `itemRank` for an item): the brief ranks "Waiting on you" by it.
   * Absent for other types.
   */
  rank: Schema.optionalKey(Schema.Number),
  /** The open items it holds up (the brief's "blocks 10"). */
  holds: Schema.optionalKey(Schema.Int),
})
export type RefView = typeof RefView.Type

/** GET /api/workspaces/:workspaceId/projects/:projectId/refs?r=P-210,DIC-103 */
export const RefLookup = Schema.Struct({ refs: Schema.Array(RefView) })
export type RefLookup = typeof RefLookup.Type

/** At most this many refs in one lookup. */
export const REF_LOOKUP_MAX = 40

/** POST …/cards/:proposalId/decide: decide a card from Home, the bell or the brief (the same command as Needs you). */
export const CardDecideBody = Schema.Struct({
  revision: Schema.Int,
  contentHash: Schema.String,
  decision: ProposalDecision,
})
export type CardDecideBody = typeof CardDecideBody.Type

/** POST …/cards/:proposalId/recommended: the push action (the recommended answer, re-checked on the server). */
export const CardRecommendedBody = Schema.Struct({ revision: Schema.Int })
export type CardRecommendedBody = typeof CardRecommendedBody.Type

/** The answer of both: decided (with the new card shape), or why not. */
export const CardDecideResult = Schema.Struct({
  ok: Schema.Boolean,
  error: Schema.optionalKey(Schema.String),
  card: Schema.optionalKey(CardShape),
})
export type CardDecideResult = typeof CardDecideResult.Type
